import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize, map, Observable } from 'rxjs';
import { NotificationItem, NotificationPriority, NotificationsListFilters, NotificationsSummary } from './data-access/notifications.model';
import { NotificationsApiService } from './data-access/notifications-api.service';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    public readonly notificationsData = signal<NotificationsSummary | null>(null);
    public readonly notificationsListData = signal<PaginatedResponse<NotificationItem> | null>(null);
    private readonly _summaryRequestsInFlight = signal<number>(0);
    private readonly _listRequestsInFlight = signal<number>(0);
    public readonly notificationsSummaryLoading = computed(() => this._summaryRequestsInFlight() > 0);
    public readonly notificationsListLoading = computed(() => this._listRequestsInFlight() > 0);
    public readonly unreadCount = computed(() => this.notificationsData()?.totalUnread || 0);

    private readonly _notificationsApiService = inject(NotificationsApiService);

    public getNotificationsSummary$(): Observable<NotificationsSummary> {
        this._summaryRequestsInFlight.update((count) => count + 1);
        return this._notificationsApiService.getNotificationsSummary().pipe(
            map((response) => {
                const summary: NotificationsSummary = {
                    totalUnread: response?.totalUnread ?? 0,
                    totalHighPriority: response?.totalHighPriority ?? 0,
                    totalCritical: response?.totalCritical ?? 0,
                    lastNotificationDate: response?.lastNotificationDate ?? null,
                    recentNotifications: response?.recentNotifications ?? []
                };
                this.notificationsData.set(summary);
                return summary;
            }),
            finalize(() => this._summaryRequestsInFlight.update((count) => Math.max(0, count - 1)))
        );
    }

    public getNotificationsSummary(): void {
        this.getNotificationsSummary$().subscribe({
            error: (error) => {
                console.error('Failed to load notifications summary:', error);
            }
        });
    }

    /** Loads paginated notifications list API and stores the full paginated response. */
    public getNotificationsList(
        page: number = 1,
        pageSize: number = 20,
        filters: NotificationsListFilters = {},
        options?: { silent?: boolean },
    ): void {
        const silent = options?.silent === true;
        if (!silent) this._listRequestsInFlight.update((count) => count + 1);
        this._notificationsApiService
            .getNotifications(page, pageSize, filters)
            .pipe(
                finalize(() => {
                    if (!silent) this._listRequestsInFlight.update((count) => Math.max(0, count - 1));
                }),
            )
            .subscribe({
                next: (notifications) => {
                    this.notificationsListData.set(notifications ?? { items: [], total: 0, page, pageSize });
                },
                error: (error) => {
                    console.error('Failed to load notifications list:', error);
                }
            });
    }

    public getUnreadCount(): number {
        return this.unreadCount();
    }

    public getTotalCount(): number {
        return this.notificationsData()?.recentNotifications?.length || 0;
    }

    public getHighPriorityCount(): number {
        return this.notificationsData()?.totalHighPriority || 0;
    }

    public getCriticalCount(): number {
        return this.notificationsData()?.totalCritical || 0;
    }

    public getLastNotificationDate(): string | null {
        return this.notificationsData()?.lastNotificationDate || null;
    }

    public getRecentNotifications(): NotificationItem[] {
        return this.notificationsData()?.recentNotifications || [];
    }

    public getNotificationsListItems(): NotificationItem[] {
        return this.notificationsListData()?.items ?? [];
    }

    public markAllNotificationsAsRead(readAll: boolean, notificationIds: number[] = [], onSuccess?: () => void): void {
        this._notificationsApiService.markAllNotificationsAsRead(readAll, notificationIds).subscribe({
            next: () => {
                onSuccess?.();
            },
            error: (error) => {
                console.error('Failed to mark notifications as read:', error);
            }
        });
    }

    /**
     * Live push (from the SignalR hub). Prepends a newly-received notification into the
     * in-memory signals so the bell badge/summary and the /notifications list update in
     * real time, regardless of which page the user is currently on. De-duplicates by id.
     */
    public prependLiveNotification(item: NotificationItem): void {
        if (!item || item.id == null) {
            return;
        }

        this.notificationsData.update((curr) => {
            const recent = curr?.recentNotifications ?? [];
            if (recent.some((n) => n.id === item.id)) {
                return curr;
            }
            const summary: NotificationsSummary = {
                totalUnread: (curr?.totalUnread ?? 0) + (item.isRead ? 0 : 1),
                totalHighPriority: (curr?.totalHighPriority ?? 0) + (item.priority === NotificationPriority.HIGH ? 1 : 0),
                totalCritical: (curr?.totalCritical ?? 0) + (item.priority === NotificationPriority.CRITICAL ? 1 : 0),
                lastNotificationDate: item.createdAt ?? curr?.lastNotificationDate ?? null,
                recentNotifications: [item, ...recent].slice(0, 20)
            };
            return summary;
        });

        this.notificationsListData.update((curr) => {
            if (!curr) {
                return curr;
            }
            if (curr.items.some((n) => n.id === item.id)) {
                return curr;
            }
            return {
                ...curr,
                items: [item, ...curr.items],
                total: curr.total + 1
            };
        });
    }

    public markNotificationAsRead$(notificationId: number): Observable<any> {
        return this._notificationsApiService.markNotificationAsRead(notificationId);
    }

    public markNotificationAsRead(notificationId: number, onSuccess?: () => void): void {
        this.markNotificationAsRead$(notificationId).subscribe({
            next: () => {
                onSuccess?.();
            },
            error: (error) => {
                console.error('Failed to mark notification as read:', error);
            }
        });
    }
}
